import * as React from 'react';

import { Loader2, Search } from 'lucide-react';

import { cn } from '../../../lib/utils';
import { Input } from '../../ui/input';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface SearchInputProps {
  /** Mirrors the native `aria-activedescendant` attribute — id of the active combobox option, if any. */
  ariaActivedescendant?: React.AriaAttributes['aria-activedescendant'];
  /** Mirrors the native `aria-autocomplete` attribute — set when the input drives a combobox listbox. */
  ariaAutocomplete?: React.AriaAttributes['aria-autocomplete'];
  /** Mirrors the native `aria-controls` attribute — id of the listbox the combobox controls. */
  ariaControls?: React.AriaAttributes['aria-controls'];
  /** Mirrors the native `aria-expanded` attribute — whether the combobox's listbox panel is open. */
  ariaExpanded?: React.AriaAttributes['aria-expanded'];
  ariaLabel?: string;
  className?: string;
  inputClassName?: string;
  /** Renders a trailing spinner (in place of nothing) while a search request is in flight. */
  isLoading?: boolean;
  onChange: (value: string) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  placeholder?: string;
  /** Mirrors the native `role` attribute — set when the input drives a combobox listbox. */
  role?: React.AriaRole;
  value: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Search input with a leading search icon.
 * Extracts the repeated `<div className="relative"><Search /><Input /></div>` pattern
 * found across all 12 action bar files. Optional `onKeyDown`, `isLoading`, and combobox aria props
 * (`role`, `ariaAutocomplete`, `ariaExpanded`, `ariaControls`, `ariaActivedescendant`) let it also back
 * combobox-style search fields (e.g. keyboard-navigable result lists) without forking the component.
 */
export function SearchInput({
  ariaActivedescendant,
  ariaAutocomplete,
  ariaControls,
  ariaExpanded,
  ariaLabel,
  className,
  inputClassName,
  isLoading,
  onChange,
  onKeyDown,
  placeholder,
  role,
  value,
}: SearchInputProps): React.JSX.Element {
  return (
    <div className={cn('relative', className)}>
      <Search className="absolute start-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <Input
        aria-activedescendant={ariaActivedescendant}
        aria-autocomplete={ariaAutocomplete}
        aria-controls={ariaControls}
        aria-expanded={ariaExpanded}
        aria-label={ariaLabel}
        className={cn('h-9 ps-9', isLoading && 'pe-9', inputClassName)}
        onChange={(e): void => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        role={role}
        value={value}
      />
      {isLoading && (
        <Loader2
          aria-hidden="true"
          className="absolute end-2.5 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground"
        />
      )}
    </div>
  );
}
